/**
 * CopyLinkRow - Row component for copying share link to clipboard
 * Feature: 080-critique-sharing
 * Task: T022
 *
 * Displays a share link with a copy button. When clicked, copies the URL
 * to clipboard and shows a success state.
 */

import { useState, useCallback } from 'react';
import { Button } from '@/components/atoms/Button';
import { cn } from '@/lib/utils';
import logger from '@/utils/logger';

export interface CopyLinkRowProps {
  /** The share URL to display and copy */
  shareUrl: string;
  /** Callback when link is copied */
  onCopy?: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Copy share link row with visual feedback
 *
 * Usage:
 *   <CopyLinkRow
 *     shareUrl="https://www.206mp.com/share/abc123..."
 *     onCopy={handleCopySuccess}
 *   />
 */
export function CopyLinkRow({ shareUrl, onCopy, className }: CopyLinkRowProps) {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setError(null);
      logger.info('Sharing', 'Share link copied to clipboard');
      onCopy?.();

      // Reset copied state after 2 seconds
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to copy';
      setError(errorMessage);
      logger.error('Sharing', 'Failed to copy share link', { error: errorMessage });
    }
  }, [shareUrl, onCopy]);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            readOnly
            value={shareUrl}
            className={cn(
              'w-full px-3 py-2 rounded-md text-sm font-mono',
              'bg-muted/50 border border-border',
              'text-foreground truncate',
              'focus:outline-none focus:ring-2 focus:ring-ring'
            )}
            onClick={(e) => (e.target as HTMLInputElement).select()}
          />
        </div>
        <Button
          variant={copied ? 'primary' : 'secondary'}
          size="md"
          onClick={handleCopy}
          className="flex items-center gap-2 shrink-0"
        >
          {copied ? (
            <>
              <CheckIcon className="h-4 w-4" />
              Copied!
            </>
          ) : (
            <>
              <CopyIcon className="h-4 w-4" />
              Copy
            </>
          )}
        </Button>
      </div>
      {error && (
        <p className="text-sm text-red-500">{error}</p>
      )}
    </div>
  );
}

// Simple copy icon
function CopyIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

// Simple check icon
function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
